// usePremium Hook
// Provides subscription status and usage quotas throughout the app
// NOTE: Currently set to FREE-FOR-ALL mode for testing. Remove override to restore premium logic.

import { useState, useEffect, useCallback } from "react";
import { supabase } from "../services/supabase";
import {
    getSubscription,
    getDailyUsage,
    type Subscription,
    type UsageQuota,
} from "../services/subscriptionService";

interface UsePremiumReturn {
    isPremium: boolean;
    isLoading: boolean;
    subscription: Subscription | null;
    // Live usage counters
    aiTips: UsageQuota;
    smartTasks: UsageQuota;
    // Actions
    refetch: () => void;
}

// FREE-FOR-ALL MODE: Unlimited quotas for testing
const UNLIMITED_QUOTA: UsageQuota = {
    used: 0,
    limit: Infinity,
    remaining: Infinity,
    unlimited: true,
};

export function usePremium(): UsePremiumReturn {
    // ============================================================
    // FREE-FOR-ALL MODE: Everyone is premium for testing
    // To restore premium logic, remove this block and uncomment below
    // ============================================================
    return {
        isPremium: true,
        isLoading: false,
        subscription: null,
        aiTips: UNLIMITED_QUOTA,
        smartTasks: UNLIMITED_QUOTA,
        refetch: () => {},
    };

    // ============================================================
    // ORIGINAL PREMIUM LOGIC (preserved for future use)
    // ============================================================
    /*
    const [isLoading, setIsLoading] = useState(true);
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [aiTips, setAiTips] = useState<UsageQuota>({
        used: 0,
        limit: 3,
        remaining: 3,
        unlimited: false,
    });
    const [smartTasks, setSmartTasks] = useState<UsageQuota>({
        used: 0,
        limit: 1,
        remaining: 1,
        unlimited: false,
    });

    const fetchData = useCallback(async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                setIsLoading(false);
                return;
            }

            const [sub, usage] = await Promise.all([
                getSubscription(user.id),
                getDailyUsage(user.id),
            ]);

            setSubscription(sub);
            setAiTips(usage.aiTips);
            setSmartTasks(usage.smartTasks);
        } catch (error) {
            console.error("Error fetching premium status:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();

        const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(() => {
            fetchData();
        });

        const handleUsageUpdate = () => fetchData();
        window.addEventListener("triyo:usage-updated", handleUsageUpdate);

        return () => {
            authSub.unsubscribe();
            window.removeEventListener("triyo:usage-updated", handleUsageUpdate);
        };
    }, [fetchData]);

    const isPremium = subscription?.status === "active" ||
        (subscription?.status === "cancelled" &&
            subscription.currentPeriodEnd &&
            new Date(subscription.currentPeriodEnd) > new Date());

    return {
        isPremium,
        isLoading,
        subscription,
        aiTips,
        smartTasks,
        refetch: fetchData,
    };
    */
}

// Helper to trigger usage update across the app
export function notifyUsageUpdate(): void {
    window.dispatchEvent(new CustomEvent("triyo:usage-updated"));
}

